import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { apiGet } from "../lib/api";
import { anyWorkspaceToken, lastWorkspace, markWorkspaceUsed, readRecentWorkspaceIds, storeWorkspaces, type WorkspaceSession, type WorkspaceSummary } from "../lib/workspaces";
import { VerifyBusiness } from "../pages/VerifyBusiness";
import { UpgradeBanner } from "./UpgradeBanner";
import { OverAllowanceWarning } from "./OverAllowanceWarning";
import type { CheckInAllowance } from "./OverAllowanceWarning";
import {
  LayoutDashboard,
  MapPin,
  Settings,
  BarChart3,
  CreditCard,
  LogOut,
  Users,
  Code2,
  Shield,
  Building2,
  Sparkles,
  Loader2,
  Menu,
  X,
  CalendarDays,
  ChevronDown,
  Search,
  Check,
  Globe2,
} from "lucide-react";

interface VerificationState {
  status: string;
  rejectionReason: string | null;
}

const navItems = [
  { to: "checkins", label: "Check-Ins", icon: LayoutDashboard },
  { to: "expected", label: "Expected Today", icon: CalendarDays },
  { to: "locations", label: "Locations", icon: MapPin },
  { to: "usage", label: "Usage", icon: BarChart3 },
  { to: "billing", label: "Billing", icon: CreditCard },
  { to: "team", label: "Team", icon: Users },
  { to: "developers", label: "Developers", icon: Code2 },
  { to: "settings", label: "Settings", icon: Settings },
];

const adminNavItems = [
  { to: "/admin", label: "Platform", icon: Shield },
  { to: "/admin/tenants", label: "Tenants", icon: Building2 },
];

export function Layout() {
  const { logout, isSuperAdmin, activeWorkspace, workspaces, login, refresh } = useAuth();
  const [verification, setVerification] = useState<VerificationState | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(!isSuperAdmin);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [allowance, setAllowance] = useState<CheckInAllowance | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [workspaceFilter, setWorkspaceFilter] = useState("");
  const [switchingWorkspace, setSwitchingWorkspace] = useState<string | null>(null);
  const switcherRef = useRef<HTMLDivElement>(null);
  const routeWorkspace = activeWorkspace ?? lastWorkspace();
  const workspacePrefix = routeWorkspace ? `/w/${routeWorkspace.slug}` : "";

  useEffect(() => {
    if (activeWorkspace) markWorkspaceUsed(activeWorkspace.id);
  }, [activeWorkspace]);

  useEffect(() => {
    if (!activeWorkspace || workspaces.length < 2) return;
    apiGet<WorkspaceSummary[]>("/workspaces").then((res) => {
      if (res.success && res.data) {
        storeWorkspaces(res.data);
        refresh();
      }
    });
  }, [activeWorkspace?.id]);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!switcherRef.current?.contains(event.target as Node)) setSwitcherOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const visibleWorkspaces = useMemo(() => {
    const query = workspaceFilter.trim().toLowerCase();
    return query ? workspaces.filter((workspace) => workspace.name.toLowerCase().includes(query)) : workspaces;
  }, [workspaceFilter, workspaces]);
  const recentIds = readRecentWorkspaceIds();
  const recent = recentIds.map((id) => visibleWorkspaces.find((workspace) => workspace.id === id)).filter((workspace): workspace is WorkspaceSummary => Boolean(workspace));
  const remaining = visibleWorkspaces.filter((workspace) => !recentIds.includes(workspace.id));

  async function switchTo(workspace: WorkspaceSummary) {
    if (!workspace.isActive || workspace.id === activeWorkspace?.id) return;
    setSwitchingWorkspace(workspace.id);
    const response = await fetch("/api/dashboard/auth/switch", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${anyWorkspaceToken()}` },
      body: JSON.stringify({ tenantId: workspace.id }),
    });
    const result = await response.json();
    if (result.success && result.data) {
      login(result.data as WorkspaceSession);
      setSwitcherOpen(false);
      navigate(`/w/${workspace.slug}/${location.pathname.split("/").slice(3).join("/") || "checkins"}`);
    }
    setSwitchingWorkspace(null);
  }

  // Close sidebar on navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isSuperAdmin) {
      setCheckingVerification(false);
      return;
    }
    setCheckingVerification(true);
    setVerification(null);
    let active = true;
    apiGet<VerificationState>("/verification").then((res) => {
      if (!active) return;
      if (res.success && res.data) setVerification(res.data);
      setCheckingVerification(false);
    });
    return () => {
      active = false;
    };
  }, [isSuperAdmin, activeWorkspace?.id]);

  useEffect(() => {
    if (isSuperAdmin) return;

    setAllowance(null);
    let active = true;
    apiGet<CheckInAllowance>("/billing").then((res) => {
      if (active && res.success && res.data) setAllowance(res.data);
    });
    return () => {
      active = false;
    };
  }, [isSuperAdmin, activeWorkspace?.id]);

  const gated = !isSuperAdmin && verification != null && verification.status !== "APPROVED";

  if (checkingVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-6 h-6 text-brand-500 animate-spin" />
      </div>
    );
  }

  if (gated) {
    return (
      <div className="relative">
        <button
          onClick={logout}
          className="absolute top-4 right-4 z-10 flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-stone-500 hover:bg-stone-100 hover:text-stone-900"
        >
          <LogOut size={16} />
          Sign Out
        </button>
        <VerifyBusiness
          status={verification!.status}
          rejectionReason={verification!.rejectionReason}
          onApproved={() => setVerification({ status: "APPROVED", rejectionReason: null })}
        />
      </div>
    );
  }

  const sidebarContent = (
    <>
      <div className="p-4 border-b border-stone-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 px-1">
            <img src="/singvms-seal.svg" alt="SingVMS" className="w-8 h-8" />
            <h1 className="text-lg font-semibold text-stone-900 font-display">SingVMS</h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-1 hover:bg-stone-100 rounded-lg"
          >
            <X size={20} />
          </button>
        </div>
        {activeWorkspace && workspaces.length > 1 && (
          <div ref={switcherRef} className="relative mt-3">
            <button
              type="button"
              onClick={() => setSwitcherOpen((open) => !open)}
              className="flex w-full items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-2 text-left hover:bg-stone-100"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white">
                {activeWorkspace.logoUrl ? <img src={activeWorkspace.logoUrl} alt="" className="h-full w-full object-contain" /> : <Building2 size={15} className="text-stone-400" />}
              </div>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-stone-800">{activeWorkspace.name}</span>
              <ChevronDown size={15} className="text-stone-400" />
            </button>
            {switcherOpen && (
              <div className="absolute left-0 top-full z-50 mt-2 w-72 rounded-xl border border-stone-200 bg-white p-2 shadow-xl">
                {workspaces.length > 8 && (
                  <div className="relative mb-2">
                    <Search size={14} className="absolute left-2.5 top-2.5 text-stone-400" />
                    <input autoFocus value={workspaceFilter} onChange={(event) => setWorkspaceFilter(event.target.value)} placeholder="Filter workspaces" className="w-full rounded-lg border border-stone-200 py-2 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-brand-500" />
                  </div>
                )}
                <button type="button" onClick={() => { setSwitcherOpen(false); navigate(`${workspacePrefix}/all-sites`); }} className="mb-1 flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50">
                  <Globe2 size={16} className="text-brand-500" /> All sites
                </button>
                {[{ label: "Recently used", items: recent }, { label: recent.length ? "All workspaces" : "Workspaces", items: remaining }].map((group) => group.items.length > 0 && (
                  <div key={group.label}>
                    <p className="px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-stone-400">{group.label}</p>
                    {group.items.map((workspace) => (
                      <button key={workspace.id} type="button" disabled={!workspace.isActive || switchingWorkspace !== null} onClick={() => switchTo(workspace)} className="flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left hover:bg-stone-50 disabled:opacity-50">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-stone-100">{workspace.logoUrl ? <img src={workspace.logoUrl} alt="" className="h-full w-full object-contain" /> : <Building2 size={14} className="text-stone-400" />}</div>
                        <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-stone-800">{workspace.name}</p><p className="text-[11px] capitalize text-stone-400">{workspace.isActive ? workspace.role.toLowerCase() : "Inactive"}</p></div>
                        {workspace.id === activeWorkspace.id && <Check size={15} className="text-brand-500" />}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {navItems.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={`${workspacePrefix}/${to}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? "bg-brand-50 text-brand-700"
                  : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
              }`
            }
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
        {isSuperAdmin && (
          <>
            <div className="pt-3 pb-1 px-3">
              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider">Admin</p>
            </div>
            {adminNavItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/admin"}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-amber-50 text-amber-700"
                      : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                  }`
                }
              >
                <Icon size={18} />
                {label}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="p-3 border-t border-stone-200 space-y-1">
        {!isSuperAdmin && (
          <NavLink
            to={`${workspacePrefix}/billing`}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-brand-700 hover:bg-brand-50 w-full"
          >
            <Sparkles size={18} />
            Upgrade
          </NavLink>
        )}
        <button
          onClick={logout}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-stone-600 hover:bg-stone-100 hover:text-stone-900 w-full"
        >
          <LogOut size={18} />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-stone-50">
      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 z-30 flex items-center gap-3 px-4 py-3 bg-white border-b border-stone-200 md:hidden">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-1 hover:bg-stone-100 rounded-lg"
        >
          <Menu size={22} />
        </button>
        <img src="/singvms-seal.svg" alt="SingVMS" className="w-7 h-7" />
        <h1 className="text-base font-semibold text-stone-900 font-display">SingVMS</h1>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — always visible on md+, slide-in drawer on mobile */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-60 bg-white border-r border-stone-200 flex flex-col transform transition-transform duration-200 ease-in-out md:relative md:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {sidebarContent}
      </aside>

      <main className="flex-1 overflow-auto flex flex-col pt-14 md:pt-0">
        {!isSuperAdmin && <UpgradeBanner />}
        {!isSuperAdmin && !location.pathname.endsWith("/billing") && (
          <OverAllowanceWarning allowance={allowance} className="mx-4 mt-4 sm:mx-6 sm:mt-6" />
        )}
        <div className="flex-1">
          <div key={activeWorkspace?.id} className="h-full"><Outlet /></div>
        </div>
      </main>
    </div>
  );
}
